import { useState } from "react";
import { useWorkspace } from "../app/workspace";
import { num } from "../lib/format";
import type { FieldType } from "../lib/types";
import { Icon } from "../components/icons";

const typeColor: Record<FieldType, string> = {
  text: "#9B9587",
  longtext: "#7A6E58",
  number: "#4A72A8",
  currency: "#3F8F63",
  percent: "#C9821A",
  date: "#8A5A9E",
  select: "#4E8C8A",
  boolean: "#BB472C",
  reference: "#B0672E",
  tags: "#C0672E",
  email: "#5C6FA8",
};

const sentimentBadge: Record<"positive" | "neutral" | "negative", string> = {
  positive: "badge--good",
  neutral: "badge--neutral",
  negative: "badge--risk",
};

export default function DataStudio() {
  const { ws } = useWorkspace();
  const [activeName, setActiveName] = useState<string>("account");

  const active = ws.objects.find((o) => o.apiName === activeName) ?? ws.objects[0];
  const customFields = active.fields.filter((f) => f.isCustom).length;

  return (
    <div className="page">
      <div className="mb-24">
        <p className="eyebrow">Data Studio</p>
        <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Model your customers the way they actually are</h1>
        <p className="page__intro">
          Objects, fields, and relationships you define — no schema migration, no engineering ticket.
          Every field you add is instantly live across health scores, segments, and playbooks.
        </p>
      </div>

      <div className="ds-layout">
        {/* Object sidebar */}
        <div className="card">
          <div className="card__hd" style={{ padding: "14px 14px" }}>
            <h3 style={{ fontSize: 13.5 }}>Objects</h3>
            <span className="tag">{ws.objects.length}</span>
          </div>
          <div style={{ padding: 8 }}>
            {(["structured", "unstructured"] as const).map((kind) => {
              const objs = ws.objects.filter((o) => o.kind === kind);
              if (!objs.length) return null;
              return (
                <div key={kind} style={{ marginBottom: 6 }}>
                  <div className="rail__label" style={{ color: "var(--faint)", padding: "8px 8px 6px", display: "flex", alignItems: "center", gap: 6 }}>
                    <Icon name={kind === "structured" ? "grid" : "sparkle"} size={12} />
                    {kind}
                  </div>
                  {objs.map((o) => (
                    <button
                      key={o.apiName}
                      onClick={() => setActiveName(o.apiName)}
                      className={`ds-obj${o.apiName === active.apiName ? " active" : ""}`}
                      style={{ width: "100%", border: "none", background: o.apiName === active.apiName ? undefined : "transparent" }}
                    >
                      <Icon name={o.icon} size={17} />
                      <span>{o.labelPlural}</span>
                      <span className="ct">{num(o.recordCount)}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
          <div style={{ padding: "4px 8px 12px" }}>
            <button className="btn btn--ghost btn--sm" style={{ width: "100%", justifyContent: "center" }}>
              <Icon name="plus" size={14} /> New object
            </button>
          </div>
        </div>

        {/* Field editor */}
        <div style={{ display: "grid", gap: 20 }}>
          <div className="card">
            <div className="card__hd">
              <div>
                <div className="row" style={{ gap: 10 }}>
                  <span className="mono" style={{ width: 34, height: 34, borderRadius: 9 }}>
                    <Icon name={active.icon} size={17} />
                  </span>
                  <div>
                    <div className="row" style={{ gap: 10 }}>
                      <h3 style={{ fontSize: 16 }}>{active.label}</h3>
                      <span className="tag">{active.apiName}</span>
                      <span className={`badge ${active.kind === "unstructured" ? "badge--watch" : "badge--up"}`}>
                        {active.kind}
                      </span>
                      <span className="badge badge--neutral">{active.system ? "System" : "Custom"}</span>
                    </div>
                    <span className="sub">
                      {num(active.recordCount)} records · {active.fields.length} fields · {customFields} custom
                    </span>
                  </div>
                </div>
              </div>
              <button className="btn btn--ghost btn--sm"><Icon name="plus" size={14} /> New field</button>
            </div>

            {/* Field header row */}
            <div
              className="field-row"
              style={{ fontFamily: "var(--mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--faint)" }}
            >
              <span>Field</span>
              <span>Type</span>
              <span>Source</span>
              <span />
            </div>

            {active.fields.map((f) => (
              <div className="field-row" key={f.key}>
                <div>
                  <b style={{ fontWeight: 600 }}>{f.label}</b>
                  <div className="fkey faint">{f.key}</div>
                </div>
                <span className="type-pill">
                  <span className="sw" style={{ background: typeColor[f.type] }} />
                  {f.type}
                </span>
                <span>
                  <span className={`badge ${f.isCustom ? "badge--good" : "badge--neutral"}`}>
                    {f.isCustom ? "Custom" : "System"}
                  </span>
                </span>
                <span style={{ justifySelf: "end" }}>
                  {f.usedInScore && (
                    <span className="chip chip--active"><Icon name="pulse" size={12} /> in health score</span>
                  )}
                </span>
              </div>
            ))}
          </div>

          {/* Unstructured document preview */}
          {active.kind === "unstructured" && active.samples && active.samples.length > 0 && (
            <div className="card">
              <div className="card__hd">
                <div>
                  <h3 style={{ fontSize: 15 }}>Enriched documents</h3>
                  <span className="sub">Raw text in, structured signal out — sentiment, themes, and a summary on every record</span>
                </div>
                <span className="chip chip--active"><Icon name="sparkle" size={12} /> auto-extracted</span>
              </div>
              <div className="card__bd" style={{ display: "grid", gap: 12 }}>
                {active.samples.map((doc) => (
                  <div key={doc.id} style={{ border: "1px solid var(--line)", borderRadius: 10, padding: 14 }}>
                    <div className="row between" style={{ alignItems: "flex-start" }}>
                      <div>
                        <b style={{ fontSize: 14 }}>{doc.title}</b>
                        <div className="faint" style={{ fontSize: 12, marginTop: 2 }}>
                          <span className="tag" style={{ marginRight: 6 }}>{doc.source}</span>
                          {doc.accountName}
                        </div>
                      </div>
                      <span className={`badge ${sentimentBadge[doc.sentiment]}`}>{doc.sentiment}</span>
                    </div>
                    <p className="muted" style={{ fontSize: 13, fontStyle: "italic", margin: "10px 0" }}>“{doc.excerpt}”</p>
                    <div className="row" style={{ gap: 10, alignItems: "flex-start" }}>
                      <span className="ff-mono faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.08em", paddingTop: 3 }}>themes</span>
                      <div className="row wrap grow" style={{ gap: 6 }}>
                        {doc.themes.map((t) => <span key={t} className="chip">{t}</span>)}
                      </div>
                    </div>
                    <div className="row" style={{ gap: 10, marginTop: 8, alignItems: "flex-start" }}>
                      <span className="ff-mono faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.08em", paddingTop: 1 }}>summary</span>
                      <span className="grow" style={{ fontSize: 13 }}>{doc.summary}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Explanatory callout */}
          <div className="card" style={{ background: "var(--corona-soft)", borderColor: "rgba(201,130,26,.3)" }}>
            <div className="card__bd row" style={{ gap: 14, alignItems: "flex-start" }}>
              <span className="mono" style={{ width: 40, height: 40, borderRadius: 11, background: "var(--surface)", color: "var(--corona-ink)" }}>
                <Icon name="bolt" size={18} />
              </span>
              <div>
                <b style={{ fontSize: 14.5, fontWeight: 600 }}>Every field is a first-class citizen</b>
                <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                  A custom field defined here is immediately usable in health scores, segments, and playbook triggers.
                  Flag it <span className="ff-mono" style={{ color: "var(--corona-ink)" }}>usedInScore</span> and it starts weighting
                  the model on the next recompute — no migration, no data team in the loop.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
